import { Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { combineLatest, firstValueFrom, lastValueFrom, timer } from 'rxjs';
import { defaultIfEmpty, distinctUntilChanged, filter, map } from 'rxjs/operators';

import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';

import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { Account, IAccount } from '@services/accounts.service';
import { IntegrationsService, IIntegrationType, IWebhookIntegration } from '@services/integrations.service';
import { NeuronsensorsService, nsISystem, nsWebhookConfig, SensorPayload } from '@services/neuronsensors.service';
import { IOrganisation } from '@services/organisation-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IIntegratonSensor, IntegrationSensorsService } from '@services/integration-sensors.service';
import { Fields, FieldSettingsModel, ListViewComponent } from '@syncfusion/ej2-angular-lists';
import { Location } from '@angular/common';
import { ItemModel, MenuEventArgs } from '@syncfusion/ej2-angular-splitbuttons';

/**
 * WebhookSensor
 */
export class WebhookSensor {
	public publicSensorID: string;
	public sensorName: string;
	public sensorDescription: string;
	public type: string;
	public neuronSensorType: number = 0;
	public dataPoints: SensorDatapoint[];

	constructor(init?: Partial<WebhookSensor>) {
		Object.assign(this, init);
	}
}

/**
 * SensorDatapoint
 */
export class SensorDatapoint {
	/**
	 * publicSenorID
	 * 
	 * Yes::: 
	 * It does have a typo, but it is on the Azure Functions and SQL, so cant fix yet
	 */
	public publicSenorID: string = '';

	/**
	 * sensorName
	 */
	public name: string = '';

	/**
	 * pvName
	 */
	public pvName: string = '';

	/**
	 * description
	 */
	public description: string = '';

	/**
	 * pv
	 */
	public pv: number = 0;

	/**
	 * si
	 */
	public si: string = '';

	/**
	 * timestamp
	 */
	public timestamp: number = 0;

	/**
	 * value
	 */
	public value: number = 0;

	/**
	 * Neuron Sensor Type
	 */
	public neuronSensorType: number = 0;

	constructor(init?: Partial<SensorDatapoint>) {
		Object.assign(this, init);
	}
}

/**
 * INeuronSensorConfiguration
 * Interface representing the configuration of a Neuron Sensor.
 * It includes properties for the sensor's serial number, alias, description,
 * and flags indicating whether it is a Neuron Sensor, obsolete, or configured.
 */
export interface INeuronSensorConfiguration {
	sn: string;
	alias: string;
	description: string;
	isNeuronSensor: boolean;
	isObsolete: boolean;
	isConfigured: boolean;
	GroupBy?: string;
	offline: boolean;
	img: string;
	sensorID: number;
	modified: number
}

/**
 * DataIntegrationsNeuronsensorsManageComponent
 */
@Component({
	selector: 'app-data-integrations-neuronsensors-manage',
	templateUrl: './data-integrations-neuronsensors-manage.component.html',
	styleUrl: './data-integrations-neuronsensors-manage.component.scss'
})
export class DataIntegrationsNeuronsensorsManageComponent {

	/** Path to Sensor Images */
	public imagePathSensors(): string {
		return imagePath(this.selectedAccountGUID, imgTypes.sensors).toLowerCase()
	}

	/** Available Sensors */
	availableSensors: INeuronSensorConfiguration[] = [];
	/** Configured Sensors */
	configuredSensors: INeuronSensorConfiguration[] = [];

	/** Selected Sensors */
	selectedAvailableSensor: INeuronSensorConfiguration | undefined;
	/** Selected Configured Sensor */
	selectedConfiguredSensor: INeuronSensorConfiguration | undefined;

	/** Filtered Available Sensors */
	filteredAvailableSensors: INeuronSensorConfiguration[] = [];
	/** Filtered Configured Sensors */
	filteredConfiguredSensors: INeuronSensorConfiguration[] = [];

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** Current User */
	public currentUser: IWebAppUser

	/** Route Organisation ID */
	public routeOrganisationID: string

	/** Selected Organisation */
	public selectedOrganisation: IOrganisation

	/** Selected Account GUID */
	public selectedAccountGUID: string

	/** Selected Account */
	public selectedAccount: IAccount

	/** Array of Webhooks */
	public webhookIntegrations: IWebhookIntegration[]

	/** Selected Integration ID */
	public selectedIntegrationID: string = "";

	/** Selected Integration */
	public selectedIntegration: IWebhookIntegration

	/** NeuronSensorDevices */
	public NeuronSensorDevices: SensorPayload[]

	/** Array of Sensors */
	public IntegrationSensors: IIntegratonSensor[];

	/** NeuronSensorSystem */
	public NeuronSensorSystem: nsISystem | undefined;

	/** NeuronSensorConfig */
	public NeuronSensorConfig: nsWebhookConfig | undefined;

	/** availableSensorList */
	@ViewChild('availableSensorList')
	availableSensorList!: ListViewComponent;

	/** configuredSensorList */
	@ViewChild('configuredSensorList')
	configuredSensorList!: ListViewComponent;

	/** selectedAvailableSensors */
	selectedAvailableSensors: INeuronSensorConfiguration[] = [];
	/** selectedConfiguredSensors */
	selectedConfiguredSensors: INeuronSensorConfiguration[] = [];

	/** hasObsolete */
	hasObsolete: boolean = false;
	/** hasNormal */
	hasNormal: boolean = false;

	/** configuredListFields */
	public configuredListFields: FieldSettingsModel = {
		text: 'alias',
		id: 'sn',
		groupBy: "GroupBy",
	};

	/** updateProgress */
	updateProgress?: {
		text: string;
		completed: number;
		total: number;
		sensor: string;
	};

	public items: ItemModel[] = [];
	public items2: ItemModel[] = [];

	/**
	 * 
	 * @param globalFunctions 
	 * @param authenticationService 
	 * @param activatedRoute 
	 * @param siteSubscriptions 
	 * @param integrationService 
	 * @param integrationSensorsService 
	 * @param router 
	 * @param location 
	 * @param dialog 
	 * @param neuronsensorsService 
	 */
	constructor(
		private globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		private activatedRoute: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private integrationService: IntegrationsService,
		private integrationSensorsService: IntegrationSensorsService,
		private router: Router,
		private location: Location,
		public dialog: MatDialog,
		private neuronsensorsService: NeuronsensorsService
	) {
	}

	// -------------------------------------------------------------------------
	// Initialisation
	// -------------------------------------------------------------------------

	/**
	 * getChildRoute
	 * Recursively traverse the ActivatedRoute tree to find the deepest child route.
	 * @param route 
	 * @returns 
	 */
	private getChildRoute(route: ActivatedRoute): ActivatedRoute {
		while (route.firstChild) {
			route = route.firstChild;
		}

		return route;
	}

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {

		const childRoute = this.getChildRoute(this.activatedRoute);

		/*
		 * Initialise the selected Account / Organisation.
		 * These values are required before we can load the integrations.
		 */
		combineLatest([
			this.authenticationService.currentUser$,
			this.siteSubscriptions.myAccounts$,
			this.siteSubscriptions.myOrganisations$,
			this.activatedRoute.paramMap,
			childRoute.paramMap.pipe(
				map(params => params.get('integrationID') || ''),
				distinctUntilChanged(),
				takeUntilDestroyed(this.destroyRef))
		]).pipe(
			takeUntilDestroyed(this.destroyRef),
			filter(([currentUser, myAccounts, myOrganisations, paramMap, childParamMap]) =>
				!!currentUser && !!myAccounts && !!myOrganisations && !!paramMap && !!childParamMap
			)
		).subscribe({
			next: async ([currentUser, myAccounts, myOrganisations, paramMap]) => {

				try {
					// Check we have a validated User
					if (currentUser) {
						this.currentUser = <IWebAppUser>currentUser;
					}

					// Get the GUID for the selected Organisation to Manage
					if (paramMap.has('organisation')) {

						if (this.routeOrganisationID != paramMap.get('organisation')) {

							this.routeOrganisationID = paramMap.get('organisation') || "";

							if (this.routeOrganisationID != "") {

								const organisationsList = myOrganisations;

								this.selectedOrganisation = <IOrganisation>organisationsList.find(
									({ Uid }) => Uid.toUpperCase() === this.routeOrganisationID.toUpperCase()
								);
							}

							const selectedAccount = myAccounts.find(
								a => a.GUID == this.selectedOrganisation.AccountGUID
							);

							if (selectedAccount) {
								this.selectedAccount = this.siteSubscriptions.normalizeFeatures(selectedAccount) as IAccount;

								this.selectedAccountGUID = selectedAccount.GUID;
							}

							this.selectedIntegrationID = childRoute.snapshot.paramMap.get('integrationID') || "";

							await this.getIntegrations(
								this.selectedAccountGUID,
								this.selectedOrganisation.Uid
							)

							if (this.selectedIntegrationID != "") {
								this.selectedIntegration = this.webhookIntegrations.find(
									(x: IWebhookIntegration) => x.WebhookUID == this.selectedIntegrationID
								) ?? <IWebhookIntegration>{};
							}

							if (this.selectedIntegration.WebhookID != 0) {
								await this.loadAllSensors();
							}

						}
					}
				}
				catch (error) {
				}
				finally {
					this.buildConfiguredSensors();
					this.globalFunctions.displaySpinner(false);
				}

			},
			error: (error) => {
				this.globalFunctions.HandleError(
					error,
					`Error: ${this.constructor.name} → account/organisation initialisation`,
					false
				);
			}
		});

	}

	/**
	 * getIntegrations
	 * Get the Integrations for the Account Organisation filtered by Type
	 * @param accountGUID
	 * @param organisationUID
	 * @param typeID
	 */
	async getIntegrations(accountGUID: string, organisationUID: string, typeID: number = 0) {

		let queryParams = {
			"typeID": typeID
		}

		try {
			const integrations = await firstValueFrom(
				this.integrationService.getWebhookIntegrations$(accountGUID, organisationUID, "", queryParams).pipe(
					filter((integrations) => !!integrations))
			);

			if (integrations) {

				if (integrations.length == 0) {
					this.webhookIntegrations = [];
					return;
				} else {
					this.webhookIntegrations = <IWebhookIntegration[]>integrations;
				}
			}
		}
		catch (error) {
			this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationService.getWebhookIntegrations$`, false);
		} finally { this.globalFunctions.displaySpinner(false); }

	};

	/**
	 * getIntegrationSensors
	 * Retrieve the list of sensors for a given webhook integration ID.
	 * @param routeWebhookID
	 */
	async getIntegrationSensors(routeWebhookID: string): Promise<IIntegratonSensor[]> {
		try {
			// Call the Account Service to get the Organisations
			const sensors = await firstValueFrom(
				this.integrationSensorsService.getIntegrationSensors$(this.selectedAccountGUID, this.routeOrganisationID, routeWebhookID, {}).pipe(
					filter((sensors) => !!sensors)
				)
			);

			return <IIntegratonSensor[]>sensors ?? [];

		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationSensorsService.getIntegrationSensors$`, false);
			}
			return <IIntegratonSensor[]>[];
		} finally { }
	}

	/**
	 * getNeuronSensorSystem
	 * Retrieve the Neuron Sensor System configuration for a given API key and system ID
	 * @param apiKey 
	 * @param systemID 
	 * @returns 
	 */
	async getNeuronSensorSystem(apiKey: string, systemID: string): Promise<nsISystem | undefined> {

		try {
			const system = await firstValueFrom(
				this.neuronsensorsService.getSystems$(apiKey, systemID)
					.pipe(
						filter((result) => !!result)
					)
			);

			return system

		} catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.neuronsensorsService.getSystems$`, false);
			}
			return undefined;
		}
	}

	/**
	 * getNeuronSensorIntegration
	 * Retrieve the Neuron Sensor Integration configuration for a given system and webhook ID
	 * @param apiKey 
	 * @param systemID 
	 * @param webhookID 
	 * @returns 
	 */
	async getNeuronSensorIntegration(apiKey: string, systemID: string, webhookID: string): Promise<nsWebhookConfig | undefined> {

		try {
			const integration = await firstValueFrom(
				this.neuronsensorsService.getIntegrationByID$(apiKey, systemID, webhookID)
					.pipe(
						filter((result) => !!result)
					)
			);

			return integration

		} catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.neuronsensorsService.getIntegrationByID$`, false);
			}
			return undefined;
		}

	}

	/**
	 * getNeuronSensorDevices
	 * Retrieve the list of Neuron Sensor devices for a given system
	 * @param apiKey 
	 * @param systemID 
	 * @returns 
	 */
	async getNeuronSensorDevices(apiKey: string, systemID: string): Promise<SensorPayload[]> {

		try {
			const SystemDevices = await firstValueFrom(
				this.neuronsensorsService.getDDevice$(apiKey, systemID, "").pipe(
					filter((devices) => !!devices)
				))

			return <SensorPayload[]>SystemDevices ?? []

		} catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.neuronsensorsService.getIntegrationByID$`, false);
			}
			return <SensorPayload[]>[];
		}
	}

	/**
	 * buildConfiguredSensors
	 * Build the available and configured sensors based on the NeuronSensorDevices and IntegrationSensors
	 * This method creates two arrays: availableSensors and configuredSensors
	 */
	private buildConfiguredSensors(): void {

		if (this.NeuronSensorDevices && this.IntegrationSensors) {

			let now = new Date().getTime();
			let ThirtyMins = (30 * 60 * 1000)

			/** Create a Set of available Neuron Sensor Serial Numbers */
			const availableSNs = new Set(
				this.NeuronSensorDevices
					.filter(sensor => sensor.type == "SENSOR")
					.map(x => x.sn)
			);

			/** Create a Set of available Neuron Sensor Serial Numbers */
			const availableOfflineSNs = new Set(
				this.NeuronSensorDevices
					.filter(sensor =>
						sensor.type == "SENSOR" &&
						(now - sensor.sample.time) > ThirtyMins
					)
					.map(x => x.sn)
			);

			/** Create a Set of configured Sensor Serial Numbers from Webhook Integrations */
			const configuredSNs = new Set<string>(
				this.IntegrationSensors.map(x => x.PublicSensorID)
			);

			/** Create the configuredSensors array based on the integrationSensors */
			this.configuredSensors = this.IntegrationSensors
				.map(
					(sensor): INeuronSensorConfiguration => ({
						sn: sensor.PublicSensorID,
						alias: sensor.Name,
						description: sensor.Description,
						isObsolete: !availableSNs.has(sensor.PublicSensorID),
						GroupBy: !availableSNs.has(sensor.PublicSensorID)
							? 'Obsolete'
							: availableOfflineSNs.has(sensor.PublicSensorID)
								? 'Configured and Offline'
								: 'Configured',
						isConfigured: true,
						isNeuronSensor: false,
						offline: availableOfflineSNs.has(sensor.PublicSensorID),
						img: "",
						sensorID: sensor.SensorID,
						modified: sensor.Modified
					})
				);

			/** Create the availableSensors array based on the NeuronSensorDevices */
			this.availableSensors = this.NeuronSensorDevices
				.filter(sensor => sensor.type == "SENSOR")
				.map(
					(sensor): INeuronSensorConfiguration => ({
						sn: sensor.sn,
						alias: sensor.info.alias,
						description: sensor.info.description,
						isNeuronSensor: true,
						isConfigured: configuredSNs.has(sensor.sn),
						isObsolete: false,
						GroupBy: availableOfflineSNs.has(sensor.sn)
							? configuredSNs.has(sensor.sn)
								? "Configured and Offline"
								: "Available and Offline"
							: configuredSNs.has(sensor.sn)
								? "Configured"
								: "Available",
						offline: availableOfflineSNs.has(sensor.sn),
						img: sensor.info.image?.trim()
							? sensor.info.image.trim()
							: sensor.config.iconUrl,
						sensorID: 0,
						modified: 0
					})
				);

			this.items = [...new Set(
				this.configuredSensors.map(s => s.GroupBy)
			)]
				.sort()
				.map(text => ({
					text,
					id: text
				}));

			this.items2 = [...new Set(
				this.availableSensors.map(s => s.GroupBy)
			)]
				.sort()
				.map(text => ({
					text,
					id: text
				}));

		}

		this.filteredAvailableSensors = [...this.availableSensors];
		this.filteredConfiguredSensors = [...this.configuredSensors];

	}

	// -------------------------------------------------------------------------
	// Selection
	// -------------------------------------------------------------------------

	onSelectConfiguredMenuItem(event: any): void {
		this.selectConfiguredSensorsByGroup(event.item.id);
	}

	onSelectAvailableMenuItem(event: any): void {
		this.selectAvailableSensorsByGroup(event.item.id);
	}

	/**
	 * selectConfiguredSensorsByGroup
	 * @param group 
	 * @returns 
	 */
	selectConfiguredSensorsByGroup(group: string) {

		if (this.selectedConfiguredSensors.length > 0) {
			if (this.selectedConfiguredSensors.some(sensor => sensor.GroupBy == group)) {
				try { this.configuredSensorList.uncheckAllItems(); } finally { this.selectedConfiguredSensors = [] }
				return;
			}
		}

		this.selectedConfiguredSensors = this.filteredConfiguredSensors.filter(
			sensor => sensor.GroupBy == group
		);

		this.configuredSensorList.uncheckAllItems();
		this.selectedConfiguredSensors.forEach(sensor => {
			this.configuredSensorList.checkItem({ sn: sensor.sn } as Fields);
		});

	}

	/**
	 * selectAvailableSensorsByGroup
	 * @param group 
	 * @returns 
	 */
	selectAvailableSensorsByGroup(group: string) {

		if (this.selectedAvailableSensors.length > 0) {
			if (this.selectedAvailableSensors.some(sensor => sensor.GroupBy == group)) {
				try { this.availableSensorList.uncheckAllItems(); } finally { this.selectedAvailableSensors = [] }
				return;
			}
		}

		this.selectedAvailableSensors = this.filteredAvailableSensors.filter(
			sensor => sensor.GroupBy == group
		);

		this.availableSensorList.uncheckAllItems();
		this.selectedAvailableSensors.forEach(sensor => {
			this.availableSensorList.checkItem({ sn: sensor.sn } as Fields);
		});

	}

	/**
	 * selectAllConfiguredSensors
	 */
	selectAllConfiguredSensors() {

		if (this.selectedConfiguredSensors.length == this.filteredConfiguredSensors.length) {
			this.configuredSensorList.uncheckAllItems();
			this.selectedConfiguredSensors = [];
			return;
		}

		this.selectedConfiguredSensors = this.filteredConfiguredSensors

		this.configuredSensorList.uncheckAllItems();
		this.selectedConfiguredSensors.forEach(sensor => {
			this.configuredSensorList.checkItem({ sn: sensor.sn } as Fields);
		});
	}

	/**
	 * selectAllAvailableSensors
	 */
	selectAllAvailableSensors() {

		if (this.selectedAvailableSensors.length == this.filteredAvailableSensors.length) {
			this.availableSensorList.uncheckAllItems();
			this.selectedAvailableSensors = [];
			return;
		}

		this.selectedAvailableSensors = this.filteredAvailableSensors

		this.availableSensorList.uncheckAllItems();
		this.selectedAvailableSensors.forEach(sensor => {
			this.availableSensorList.checkItem({ sn: sensor.sn } as Fields);
		});
	}


	/**
	 * onSensorSelected
	 * This method handles the selection of a sensor from either the available or configured list.	 * 
	 * @param event 
	 * @param list 
	 * @returns 
	 */
	onSensorSelected(
		event: any,
		list: 'available' | 'configured'
	): void {

		const sn = event?.data?.sn;

		if (sn === undefined) {
			return;
		}

		const sensors = list === 'available'
			? this.availableSensors
			: this.configuredSensors;

		var selectedSensors = list === 'available'
			? this.selectedAvailableSensors
			: this.selectedConfiguredSensors;

		const sensor = sensors.find(x => x.sn === sn);

		if (!sensor) {
			return;
		}

		if (event.isChecked) {
			if (!selectedSensors.some(x => x.sn === sensor.sn)) {
				selectedSensors.push(sensor);
			}
		} else {
			selectedSensors = selectedSensors.filter(x => x.sn !== sensor.sn);
		}

		if (list === 'available') {
			this.selectedAvailableSensors = selectedSensors;
			this.selectedAvailableSensor = sensor;
			this.selectedConfiguredSensor = undefined;
			this.selectedConfiguredSensors = [];
			// try { this.configuredSensorList.uncheckAllItems(); } finally { }
		}
		else {
			this.selectedConfiguredSensors = selectedSensors;
			this.selectedConfiguredSensor = sensor;
			this.selectedAvailableSensor = undefined;
			this.selectedAvailableSensors = [];
			// try { this.availableSensorList.uncheckAllItems(); } finally { }

		}

	}

	// -------------------------------------------------------------------------
	// Drag & Drop
	// -------------------------------------------------------------------------

	/**
	 * Handle the drag start event when a sensor is being dragged
	 * This method should store the sensor being dragged for later use in the drop event.
	 * @param event 
	 */
	onDragStart(event: any): void {

		/** TODO: Store the sensor being dragged */

	}

	/**
	 * Handle the drop event when a sensor is dragged and dropped into a new list
	 * This method should determine the source and target lists, and move the sensor accordingly.
	 * @param event 
	 */
	onDragStop(event: any): void {

		/** TODO: Determine source and target list */

	}


	// -------------------------------------------------------------------------
	// Transfer
	// -------------------------------------------------------------------------

	/**
	 * Get the type of selection in the configured sensors list
	 * @returns 
	 */
	get configuredSelectionType(): 'none' | 'obsolete' | 'normal' | 'mixed' {

		if (!this.selectedConfiguredSensors.length) {
			return 'none';
		}

		const hasObsolete = this.selectedConfiguredSensors.some(x => x.isObsolete);
		const hasNormal = this.selectedConfiguredSensors.some(x => !x.isObsolete);

		if (hasObsolete && hasNormal) {
			return 'mixed';
		}

		return hasObsolete ? 'obsolete' : 'normal';
	}

	/**
	 * Get the type of selection in the avaliable sensors list
	 * @returns 
	 */
	get avaliableSelectionType(): 'none' | 'configured' | 'avaliable' | 'mixed' {

		if (!this.selectedAvailableSensors.length) {
			return 'none';
		}

		const hasConfigured = this.selectedAvailableSensors.some(x => x.isConfigured);
		const hasAvaliable = this.selectedAvailableSensors.some(x => !x.isConfigured);

		if (hasConfigured && hasAvaliable) {
			return 'mixed';
		}

		return hasConfigured ? 'configured' : 'avaliable';
	}

	/**
	 * Move the selected sensor from the available list to the configured list
	 * This method checks if a sensor is selected in the available list, removes it from that list,
	 * marks it as configured, and adds it to the configured list if it's not already there.
	 * It also clears the selection after the transfer.
	 * @returns 
	 */
	async moveSelectedToConfigured(sectionType: 'none' | 'configured' | 'avaliable' | 'mixed'): Promise<void> {

		try {
			const sensors = this.selectedAvailableSensors;

			if (!sensors.length) {
				return;
			}

			let message = [
				`Are you sure you want to ${sectionType == 'configured' ? 'Update' : 'Add'} the following sensors?`,
				`\n${sensors.map(x => x.alias?.trim() || x.sn).join(', ')}`,
				sectionType == 'configured' ? `\nRe configuring these sensors will overwrite Sensor Names, Descriptions and Images.` : '',
				`\nThis action cannot be undone.`
			].filter(s => !!s).join('\n');

			const OKResult = await firstValueFrom(
				this.globalFunctions.UserConfirm$(
					message,
					sectionType == 'configured' ? 'Update Sensors' : 'Add Sensors',
					sectionType == 'configured' ? 'Update' : 'Add',
					undefined,
					"750px"
				)
			);
			if (OKResult) {

				this.updateProgress = {
					text: sectionType == 'configured' ? "Updating" : "Adding ",
					completed: 0,
					sensor: '',
					total: sensors.length
				};


				for (const sensor of sensors) {

					sensor.isConfigured = true;

					var avaliableSensor = this.availableSensors.find(x => x.sn === sensor.sn);

					if (avaliableSensor) {
						avaliableSensor.isConfigured = true;
						avaliableSensor.GroupBy = "Configured";
					}

					const alreadyConfigured = this.configuredSensors.some(
						x => x.sn === sensor.sn
					);

					if (!avaliableSensor) {
						return;
					}

					try {

						if (this.updateProgress) {
							this.updateProgress.sensor = sensor.alias?.trim() || sensor.sn;
							this.updateProgress.completed++;
						}

						var newSensor = this.CreateWebhookSensor(avaliableSensor.sn)

						/** Add the sensor to the Database */
						const result = await firstValueFrom(
							this.integrationSensorsService.PutIntegrationSensor$(
								this.selectedAccountGUID,
								this.selectedOrganisation.Uid,
								this.selectedIntegrationID,
								avaliableSensor.sn,
								JSON.stringify(newSensor)
							)
						);

						if (result) {
							let JSONResult = JSON.parse(result[0].Data)
							let newSN = JSONResult['PublicID']

							if (!alreadyConfigured && newSN == avaliableSensor.sn) {
								this.configuredSensors = [
									...this.configuredSensors,
									{
										...sensor,
										isNeuronSensor: false,
										isConfigured: true,
										isObsolete: false,
										GroupBy: "Configured"
									}
								];
							}

							const message = result?.[0]?.Result?.replace("OK - ", "");

							if (message) {
								this.globalFunctions.DisplaySnackBar({
									message,
									duration: 2000,
									action: ""
								});
							}
						}
						else {
							this.globalFunctions.DisplaySnackBar({
								message: `Error Adding '${avaliableSensor.sn}'`,
								duration: 2000,
								action: ""
							});
						}

						// Give the user time to see the progress before starting the next sensor
						await this.delay(400);

					} catch (error: any) {
						this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → deleteIntegrationSensor$ → ${sensor.sn}`, false);
					}

				}
			}
		}
		catch (error) {

		}
		finally {

			/** Refresh the Integration list */
			this.IntegrationSensors = await this.getIntegrationSensors(this.selectedIntegrationID);

			/** Rebiuld the configured relationships */
			this.buildConfiguredSensors();

			/** Clear all selections */
			this.clearSelections()

			const deviceList = this.availableSensors
				?.filter(s => s.isConfigured)
				.map(s => s.sn) ?? [];

			this.patchIntegrationDeviceList(
				this.selectedIntegration.NeuronSensorAPI,
				this.selectedIntegration.NeuronSensorSystem,
				deviceList)

		}
	}


	/**
	 * Move the selected sensor from the configured list back to the available list
	 * This method checks if a sensor is selected in the configured list, removes it from that list,
	 * and adds it back to the available list if it's not already there. It also marks the sensor as not configured.
	 * Finally, it clears the selection after the transfer.
	 * @returns 
	 */
	async moveSelectedToAvailable(): Promise<void> {

		const sensors = this.selectedConfiguredSensors;//.filter(sensor => sensor.isConfigured);

		if (!sensors.length) {
			return;
		}

		let message = [
			`Are you sure you want to remove the following sensors from this integration?`,
			`\n${sensors.map(x => x.alias?.trim() || x.sn).join(', ')}`,
			`\nAssociated Datapoints and Rules will be removed from linked Assets and Locations.`,
			`This action cannot be undone.`
		].filter(s => !!s).join('\n');

		const OKResult = await firstValueFrom(
			this.globalFunctions.UserConfirm$(
				message,
				`Remove Sensors`,
				"Remove",
				undefined,
				"750px"
			)
		);
		if (OKResult) {
			try {
				await this.deleteIntegrationSensor(sensors);
			}
			catch (error: any) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → deleteObsoleteSensor`, false);
			}
		}

	}

	// -------------------------------------------------------------------------
	// Load / Reset
	// -------------------------------------------------------------------------

	/**
	 * loadAllSensors
	 * This method resets the current configuration of sensors.
	 * It clears the selected sensors and reloads the available and configured sensors.
	 * This effectively reverts any changes made to the sensor configuration since the last load.
	 */
	async loadAllSensors(): Promise<void> {

		if (!this.selectedIntegration) { return; }

		[
			this.IntegrationSensors,
			this.NeuronSensorConfig,
			this.NeuronSensorSystem,
			this.NeuronSensorDevices
		] = await Promise.all([
			this.getIntegrationSensors(
				this.selectedIntegrationID
			),
			this.getNeuronSensorIntegration(
				this.selectedIntegration.NeuronSensorAPI,
				this.selectedIntegration.NeuronSensorSystem,
				this.selectedIntegration.ClientWebhookID
			),
			this.getNeuronSensorSystem(
				this.selectedIntegration.NeuronSensorAPI,
				this.selectedIntegration.NeuronSensorSystem),
			this.getNeuronSensorDevices(
				this.selectedIntegration.NeuronSensorAPI,
				this.selectedIntegration.NeuronSensorSystem)
		]);

		this.buildConfiguredSensors();

		this.clearSelections();

	}

	/**
	 * deleteObsoleteSensor
	 * This method deletes the selected sensor from the configured list if it is marked as obsolete.
	 * It checks if a sensor is selected and if it is obsolete, then removes it from the configuredSensors array.
	 * After deletion, it clears the selection of the configured sensor.
	 * @returns 
	 */
	async deleteObsoleteSensor(): Promise<void> {

		const sensors = this.selectedConfiguredSensors.filter(sensor => sensor.isObsolete);

		if (!sensors.length) {
			return;
		}

		let message = [
			`Are you sure you want to delete the following Obsolete sensors from this integration?`,
			`\n${sensors.map(x => x.alias?.trim() || x.sn).join(', ')}`,
			`\nAssociated Datapoints and Rules will be removed from linked Assets and Locations.`,
			`This action cannot be undone.`].filter(s => !!s).join('\n');

		const OKResult = await firstValueFrom(
			this.globalFunctions.UserConfirm$(
				message,
				`Delete Obsolete Sensors`,
				"Delete",
				undefined,
				"750px"
			)
		);
		if (OKResult) {
			try {
				await this.deleteIntegrationSensor(sensors);
			}
			catch (error: any) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → deleteObsoleteSensor`, false);
			}
		}

	}

	/**
	 * deleteIntegrationSensor
	 * This method deletes the selected sensor from the configured list if it is marked as obsolete.
	 * It checks if a sensor is selected and if it is obsolete, then removes it from the configuredSensors array.
	 * After deletion, it clears the selection of the configured sensor.
	 * @returns 
	 */
	async deleteIntegrationSensor(sensors: INeuronSensorConfiguration[], obsolete: boolean = false): Promise<void> {

		try {

			if (!sensors.length) {
				return;
			}

			this.updateProgress = {
				text: "Deleting ",
				completed: 0,
				sensor: '',
				total: sensors.length
			};

			for (const sensor of sensors) {

				try {

					const integrationSensor = this.IntegrationSensors?.find(x => x.PublicSensorID === sensor.sn);

					if (!integrationSensor || !this.selectedAccountGUID || !this.selectedOrganisation?.Uid || !this.selectedIntegrationID) {
						throw new Error(`Unable to find PRISMPdM sensor record for ${sensor.sn}`);
					}

					if (this.updateProgress) {
						this.updateProgress.sensor = sensor.alias?.trim() || sensor.sn;
						this.updateProgress.completed++;
					}

					const result = await firstValueFrom(
						this.integrationSensorsService.deleteIntegrationSensor$(
							this.selectedAccountGUID,
							this.selectedOrganisation.Uid,
							this.selectedIntegrationID,
							integrationSensor.SensorID
						)
					);

					const selectedsensor = this.selectedConfiguredSensors.find(x => x.sn === sensor.sn);

					this.configuredSensors = this.configuredSensors.filter(x => x.sn !== sensor.sn);
					this.IntegrationSensors = this.IntegrationSensors?.filter(x => x.PublicSensorID !== sensor.sn);

					const existingSensor = this.availableSensors.find(x => x.sn === sensor.sn);

					if (existingSensor) {

						existingSensor.isConfigured = false;
						existingSensor.isNeuronSensor = true;
						existingSensor.isObsolete = false;
						existingSensor.GroupBy = "Available";

					} else if (!selectedsensor?.isObsolete) {

						this.availableSensors = [
							...this.availableSensors,
							{
								...sensor,
								isNeuronSensor: true,
								isConfigured: false,
								isObsolete: false,
								GroupBy: "Available"
							}
						];

					}

					const message = result?.[0]?.Result?.replace("OK - ", "");

					if (message) {
						this.globalFunctions.DisplaySnackBar({
							message,
							duration: 2000,
							action: ""
						});
					}

					// Give the user time to see the progress before starting the next sensor
					await this.delay(400);

				} catch (error: any) {

					this.globalFunctions.HandleError(
						error,
						`Error: ${this.constructor.name} → deleteIntegrationSensor$ → ${sensor.sn}`,
						false
					);

				}

			}

		} catch (error: any) {

			this.globalFunctions.HandleError(
				error,
				`Error: ${this.constructor.name} → deleteIntegrationSensor$`,
				false
			);

		} finally {

			// // Give the user time to see the final progress
			// await this.delay(1000);

			/** Refresh the Integration list */
			this.IntegrationSensors = await this.getIntegrationSensors(this.selectedIntegrationID);

			/** Rebiuld the configured relationships */
			this.buildConfiguredSensors();

			/** Clear all selections */
			this.clearSelections()

			const deviceList = this.availableSensors
				?.filter(s => s.isConfigured)
				.map(s => s.sn) ?? [];

			this.patchIntegrationDeviceList(
				this.selectedIntegration.NeuronSensorAPI,
				this.selectedIntegration.NeuronSensorSystem,
				deviceList)
		}

	}

	/**
	 * delay
	 * @param milliseconds 
	 * @returns 
	 */
	private delay(milliseconds: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, milliseconds));
	}

	/**
	 * clearSelections
	 */
	clearSelections() {

		this.selectedAvailableSensor = undefined;
		this.selectedAvailableSensors = [];

		this.selectedConfiguredSensor = undefined;
		this.selectedConfiguredSensors = [];

		try { this.configuredSensorList.uncheckAllItems(); } catch { } finally { }
		try { this.availableSensorList.uncheckAllItems(); } catch { } finally { }

		// try { this.configuredSensorList.refresh(); } finally { }
		// try { this.availableSensorList.refresh(); } finally { }

		this.updateProgress = undefined;

	}


	/**
	 * CreateWebhookSensor
	 * Create a PRISMPdM Sensor JSON object 
	 * @param sn 
	 */
	CreateWebhookSensor(sn: string): WebhookSensor {

		try {
			/** Search Neuron Sensor Devices */
			let sensor = this.NeuronSensorDevices.find(sensor => sensor.sn == sn)

			if (sensor) {
				let webhookSensor = new WebhookSensor
					({
						publicSensorID: sensor.sn,
						sensorName: sensor.info.alias.trim() ?? sensor.sn,
						sensorDescription: sensor.info.description.trim(),
						type: 'neuronsensors', //"omron",
						neuronSensorType: sensor.config.type,
						dataPoints: this.CreateSensorDatapoints(sensor)
					});

				return webhookSensor
			}
			else {
				return <WebhookSensor>{}
			}
		}
		catch (error: any) {
			console.log(`Error:${error.message}`)
			throw (error);
		}
	}

	/**
	 * 
	 * @param sensor 
	 */
	CreateSensorDatapoints(sensor: SensorPayload): SensorDatapoint[] {

		try {
			var indexedList = <SensorDatapoint[]>[];
			if (sensor.config.pv == 1) {
				if (sensor.sample.values.length > 1) {
					indexedList = [
						new SensorDatapoint({
							publicSenorID: sensor.sn,
							name: sensor.sn,
							pvName: sensor.sample.values[1].unit,
							description: "",
							pv: 1,
							si: sensor.sample.values[1].unit,
							timestamp: sensor.sample.time,
							value: sensor.sample.values[1].value,
							neuronSensorType: sensor.config.type
						}),
						new SensorDatapoint({
							publicSenorID: sensor.sn,
							name: sensor.sn,
							pvName: sensor.sample.values[0].unit,
							description: "",
							pv: 2,
							si: sensor.sample.values[0].unit,
							timestamp: sensor.sample.time,
							value: sensor.sample.values[0].value,
							neuronSensorType: sensor.config.type
						})
					];
				}
				else {
					indexedList.push(
						new SensorDatapoint({
							publicSenorID: sensor.sn,
							name: sensor.sn,
							pvName: sensor.sample.values[0].unit,
							description: '',
							pv: 1,
							si: sensor.sample.values[0].unit,
							timestamp: sensor.sample.time,
							value: sensor.sample.values[0].value,
							neuronSensorType: sensor.config.type
						})
					);
				}
			}
			else {
				indexedList = [
					new SensorDatapoint({
						publicSenorID: sensor.sn,
						name: sensor.sn,
						pvName: sensor.sample.values[0].unit,
						description: "",
						pv: 1,
						si: sensor.sample.values[0].unit,
						timestamp: sensor.sample.time,
						value: sensor.sample.values[0].value,
						neuronSensorType: sensor.config.type
					})
				];
				if (sensor.sample.values.length > 1) {
					indexedList.push(
						new SensorDatapoint({
							publicSenorID: sensor.sn,
							name: sensor.sn,
							pvName: sensor.sample.values[1].unit,
							description: '',
							pv: 2,
							si: sensor.sample.values[1].unit,
							timestamp: sensor.sample.time,
							value: sensor.sample.values[1].value,
							neuronSensorType: sensor.config.type
						})
					);
				}
			}

			return indexedList;
		}
		catch (error: any) {
			console.log(`Error:${error.message}`)
			throw (error);
		}

	}


	/**
	 * patchIntegration
	 * @param apiKey 
	 * @param systemID 
	 * @param deviceList 
	 */
	async patchIntegrationDeviceList(apiKey: string, systemID: string, deviceList: string[]) {

		try {
			if (this.NeuronSensorConfig) {

				this.NeuronSensorConfig.sensors = deviceList

				const result = await firstValueFrom(
					this.neuronsensorsService.patchIntegration$(apiKey, systemID, this.NeuronSensorConfig).pipe(
						filter((devices) => !!devices)
					))
			}

		} catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.neuronsensorsService.getIntegrationByID$`, false);
			}

		}
	}

	/**
 * filterSensors
 * Filter sensors by serial number, alias or description.
 * @param searchText
 * @param list
 */
	filterSensors(
		searchText: string,
		list: 'available' | 'configured'
	): void {

		const search = searchText?.trim().toLowerCase() || '';

		const sensors = list === 'available'
			? this.availableSensors
			: this.configuredSensors;

		const filteredSensors = !search
			? sensors
			: sensors.filter(sensor =>
				sensor.sn?.toLowerCase().includes(search) ||
				sensor.alias?.toLowerCase().includes(search) ||
				sensor.description?.toLowerCase().includes(search)
			);

		if (list === 'available') {
			this.filteredAvailableSensors = filteredSensors;
		}
		else {
			this.filteredConfiguredSensors = filteredSensors;
		}
	}

	/**
 * View the Image Fullsize
 * @param imageURL
 */
	viewImage(event: any, imageURL: string) {
		event.stopPropagation()

		if (!imageURL.includes("neuronassets")) {
			this.globalFunctions.viewImageFullSize(imageURL);
		}

	}

	/** back */
	back() {
		this.location.back();
	}
}
